/* eslint-disable */
export class PerformanceRecordsService {
  
  static initializeRecords() {
    return {
      monthlyRecords: {
        short: [],
        medium: [],
        long: [],
        earnings: []
      },
      allTimeRecords: {
        short: [],
        medium: [],
        long: [],
        earnings: []
      }
    }
  }

  static processRaceResult(raceResult, gameStore) {
    if (!raceResult || !gameStore || !gameStore.performanceRecords) {
      console.warn('Invalid race result or game store for performance tracking')
      return
    }
    
    const { circuit, raceType, participants, currentDay } = raceResult
    
    if (!participants || !Array.isArray(participants)) {
      console.warn('Invalid participants data for performance tracking')
      return
    }
    
    participants.forEach((participant, index) => {
      if (participant && participant.finishTime && participant.finishTime > 0) {
        try {
          const recordEntry = this.createRecordEntry(participant, circuit, currentDay, gameStore)
          
          // Ensure performance records structure exists
          if (!gameStore.performanceRecords.monthlyRecords) {
            gameStore.performanceRecords.monthlyRecords = { short: [], medium: [], long: [], earnings: [] }
          }
          if (!gameStore.performanceRecords.allTimeRecords) {
            gameStore.performanceRecords.allTimeRecords = { short: [], medium: [], long: [], earnings: [] }
          }
          
          // Initialize earnings arrays if they don't exist
          if (!gameStore.performanceRecords.monthlyRecords.earnings) {
            gameStore.performanceRecords.monthlyRecords.earnings = []
          }
          if (!gameStore.performanceRecords.allTimeRecords.earnings) {
            gameStore.performanceRecords.allTimeRecords.earnings = []
          }
          
          // Update both monthly and all-time records with notification support
          if (gameStore.performanceRecords.monthlyRecords[raceType]) {
            this.updateRecords(gameStore.performanceRecords.monthlyRecords[raceType], recordEntry, gameStore, 'monthly', raceType)
          }
          if (gameStore.performanceRecords.allTimeRecords[raceType]) {
            this.updateRecords(gameStore.performanceRecords.allTimeRecords[raceType], recordEntry, gameStore, 'all_time', raceType)
          }
        } catch (error) {
          console.warn('Error creating performance record entry:', error)
        }
      }
    })
    
    // Update earnings records after processing race results
    this.updateEarningsRecords(gameStore)
  }

  static createRecordEntry(participant, circuit, currentDay, gameStore) {
    if (!participant || !participant.chocobo || !participant.jockey) {
      throw new Error('Invalid participant data for record entry')
    }
    
    const chocobo = participant.chocobo
    const jockey = participant.jockey
    const owner = gameStore.owners?.find(o => o.chocoboIds?.includes(chocobo.id))
    
    return {
      chocoboName: chocobo.name || 'Unknown Chocobo',
      chocoboId: chocobo.id || 'unknown',
      chocoboColor: chocobo.color || 'yellow',
      chocoboLevel: chocobo.level || 1,
      jockeyName: jockey.name || 'Unknown Jockey',
      jockeyId: jockey.id || 'unknown',
      ownerName: owner ? (owner.isPlayer ? `${owner.name} (Player)` : owner.name) : 'Unknown',
      ownerId: owner ? owner.id : null,
      ranchName: owner ? owner.ranchName : 'Unknown Ranch',
      time: participant.finishTime || 999,
      formattedTime: participant.formattedTime || 'N/A',
      circuit: circuit?.id || 'unknown',
      circuitName: circuit?.name || 'Unknown Circuit',
      date: currentDay || 1,
      speed: participant.speed || 'N/A',
      position: participant.position || 8
    }
  }

  static updateRecords(recordList, newEntry, gameStore = null, recordType = 'monthly', raceType = 'short') {
    // Store the previous #1 record holder before updates
    const previousTopRecord = recordList[0] || null
    
    // Check if this chocobo already has a record in this distance
    const existingIndex = recordList.findIndex(record => record.chocoboId === newEntry.chocoboId)
    
    if (existingIndex !== -1) {
      // Chocobo already has a record - only update if new time is better (faster)
      if (newEntry.time < recordList[existingIndex].time) {
        const oldTime = recordList[existingIndex].time
        recordList[existingIndex] = newEntry // Replace with better time
        
        // Generate personal best notification if this is player's chocobo
        if (gameStore && newEntry.ownerId === gameStore.playerOwner?.id) {
          this.generatePerformanceNotification(gameStore, {
            type: 'new_record',
            recordType: 'personal_best',
            raceType: raceType,
            chocoboName: newEntry.chocoboName,
            chocoboId: newEntry.chocoboId,
            time: newEntry.time,
            previousRecord: oldTime,
            day: gameStore.currentDay
          })
        }
      }
      // If new time is worse, ignore it - keep the existing better time
    } else {
      // New chocobo - add the entry
      recordList.push(newEntry)
    }
    
    // Sort by time (fastest first)
    recordList.sort((a, b) => a.time - b.time)
    
    // Check if the new entry is now #1 and it's a player chocobo
    if (gameStore && recordList[0] && recordList[0].chocoboId === newEntry.chocoboId) {
      // Check if this is a new #1 record (either no previous record or beaten the previous)
      if (!previousTopRecord || newEntry.time < previousTopRecord.time) {
        if (newEntry.ownerId === gameStore.playerOwner?.id) {
          // Player set a new track record!
          this.generatePerformanceNotification(gameStore, {
            type: 'new_record',
            recordType: recordType === 'monthly' ? 'monthly' : 'all_time',
            raceType: raceType,
            chocoboName: newEntry.chocoboName,
            chocoboId: newEntry.chocoboId,
            time: newEntry.time,
            previousRecord: previousTopRecord ? previousTopRecord.time : null,
            day: gameStore.currentDay
          })
        } else if (previousTopRecord && previousTopRecord.ownerId === gameStore.playerOwner?.id) {
          // Player's record was beaten!
          this.generatePerformanceNotification(gameStore, {
            type: 'record_beaten',
            recordType: recordType === 'monthly' ? 'monthly' : 'all_time',
            raceType: raceType,
            chocoboName: previousTopRecord.chocoboName,
            chocoboId: previousTopRecord.chocoboId,
            time: previousTopRecord.time,
            beatenBy: {
              name: newEntry.chocoboName,
              time: newEntry.time
            },
            day: gameStore.currentDay
          })
        }
      }
    }
    
    // Keep only top 10
    if (recordList.length > 10) {
      recordList.splice(10)
    }
  }

  static isNewRecord(recordList, time) {
    if (recordList.length < 10) return true
    return time < recordList[recordList.length - 1].time
  }

  static getRecordPosition(recordList, time) {
    for (let i = 0; i < recordList.length; i++) {
      if (time < recordList[i].time) {
        return i + 1 // 1-based position
      }
    }
    return recordList.length < 10 ? recordList.length + 1 : null
  }
  
  static generatePerformanceNotification(gameStore, notificationData) {
    if (!gameStore.performanceNotifications) {
      gameStore.performanceNotifications = []
    }
    
    const notification = {
      id: crypto.randomUUID(),
      ...notificationData,
      read: false,
      timestamp: Date.now()
    }
    
    gameStore.performanceNotifications.unshift(notification)
    
    // Keep only last 50 performance notifications
    if (gameStore.performanceNotifications.length > 50) {
      gameStore.performanceNotifications = gameStore.performanceNotifications.slice(0, 50)
    }
  }

  static resetMonthlyRecords(performanceRecords) {
    performanceRecords.monthlyRecords = {
      short: [],
      medium: [],
      long: [],
      earnings: []
    }
  }

  static getTopPerformers(performanceRecords, type = 'monthly', raceType = 'short') {
    const records = type === 'monthly' 
      ? performanceRecords.monthlyRecords 
      : performanceRecords.allTimeRecords
    
    return records[raceType] || []
  }

  static updateEarningsRecords(gameStore) {
    if (!gameStore || !gameStore.performanceRecords) return
    
    // Calculate earnings for all chocobos
    const monthlyEarnings = {}
    const allTimeEarnings = {}
    const currentMonth = Math.floor((gameStore.currentDay - 1) / 32) + 1
    
    // Go through all chocobos and calculate their earnings
    gameStore.chocobos.forEach(chocobo => {
      if (chocobo.raceHistory && chocobo.raceHistory.length > 0) {
        let monthlyTotal = 0
        let allTimeTotal = 0
        
        chocobo.raceHistory.forEach(race => {
          const earnings = race.earnings || 0
          allTimeTotal += earnings
          
          // Check if race was in current month
          const raceMonth = Math.floor((race.day - 1) / 32) + 1
          if (raceMonth === currentMonth) {
            monthlyTotal += earnings
          }
        })
        
        if (monthlyTotal > 0) {
          monthlyEarnings[chocobo.id] = {
            chocobo,
            earnings: monthlyTotal,
            raceCount: chocobo.raceHistory.filter(r => Math.floor((r.day - 1) / 32) + 1 === currentMonth).length
          }
        }
        
        if (allTimeTotal > 0) {
          allTimeEarnings[chocobo.id] = {
            chocobo,
            earnings: allTimeTotal,
            raceCount: chocobo.raceHistory.length
          }
        }
      }
    })
    
    // Convert to arrays and sort by earnings
    const monthlyEarningsArray = Object.values(monthlyEarnings)
      .sort((a, b) => b.earnings - a.earnings)
      .slice(0, 10)
    
    const allTimeEarningsArray = Object.values(allTimeEarnings)
      .sort((a, b) => b.earnings - a.earnings)
      .slice(0, 10)
    
    // Create record entries for earnings
    gameStore.performanceRecords.monthlyRecords.earnings = monthlyEarningsArray.map(entry => {
      const owner = gameStore.owners?.find(o => o.chocoboIds?.includes(entry.chocobo.id))
      return {
        chocoboName: entry.chocobo.name || 'Unknown Chocobo',
        chocoboId: entry.chocobo.id || 'unknown',
        chocoboColor: entry.chocobo.color || 'yellow',
        chocoboLevel: entry.chocobo.level || 1,
        ownerName: owner ? (owner.isPlayer ? `${owner.name} (Player)` : owner.name) : 'Unknown',
        ownerId: owner ? owner.id : null,
        ranchName: owner ? owner.ranchName : 'Unknown Ranch',
        earnings: entry.earnings,
        raceCount: entry.raceCount,
        avgEarnings: Math.round(entry.earnings / entry.raceCount)
      }
    })
    
    gameStore.performanceRecords.allTimeRecords.earnings = allTimeEarningsArray.map(entry => {
      const owner = gameStore.owners?.find(o => o.chocoboIds?.includes(entry.chocobo.id))
      return {
        chocoboName: entry.chocobo.name || 'Unknown Chocobo',
        chocoboId: entry.chocobo.id || 'unknown',
        chocoboColor: entry.chocobo.color || 'yellow',
        chocoboLevel: entry.chocobo.level || 1,
        ownerName: owner ? (owner.isPlayer ? `${owner.name} (Player)` : owner.name) : 'Unknown',
        ownerId: owner ? owner.id : null,
        ranchName: owner ? owner.ranchName : 'Unknown Ranch',
        earnings: entry.earnings,
        raceCount: entry.raceCount,
        avgEarnings: Math.round(entry.earnings / entry.raceCount)
      }
    })
  }

  static getChocoboRecords(performanceRecords, chocoboId) {
    const records = {
      monthly: { short: null, medium: null, long: null },
      allTime: { short: null, medium: null, long: null }
    }

    // Check monthly records
    ['short', 'medium', 'long'].forEach(raceType => {
      const monthlyRecord = performanceRecords.monthlyRecords[raceType]
        .find(record => record.chocoboId === chocoboId)
      if (monthlyRecord) {
        records.monthly[raceType] = {
          position: performanceRecords.monthlyRecords[raceType].indexOf(monthlyRecord) + 1,
          ...monthlyRecord
        }
      }

      const allTimeRecord = performanceRecords.allTimeRecords[raceType]
        .find(record => record.chocoboId === chocoboId)
      if (allTimeRecord) {
        records.allTime[raceType] = {
          position: performanceRecords.allTimeRecords[raceType].indexOf(allTimeRecord) + 1,
          ...allTimeRecord
        }
      }
    })

    return records
  }

  static getOwnerRecords(performanceRecords, ownerId) {
    const ownerRecords = []

    // Check all record types
    ['monthly', 'allTime'].forEach(timeType => {
      const recordSet = timeType === 'monthly' 
        ? performanceRecords.monthlyRecords 
        : performanceRecords.allTimeRecords

      ['short', 'medium', 'long'].forEach(raceType => {
        recordSet[raceType].forEach((record, index) => {
          if (record.ownerId === ownerId) {
            ownerRecords.push({
              ...record,
              position: index + 1,
              timeType,
              raceType
            })
          }
        })
      })
    })

    return ownerRecords.sort((a, b) => a.position - b.position)
  }

  static getJockeyRecords(performanceRecords, jockeyId) {
    const jockeyRecords = []

    // Check all record types
    ['monthly', 'allTime'].forEach(timeType => {
      const recordSet = timeType === 'monthly' 
        ? performanceRecords.monthlyRecords 
        : performanceRecords.allTimeRecords

      ['short', 'medium', 'long'].forEach(raceType => {
        recordSet[raceType].forEach((record, index) => {
          if (record.jockeyId === jockeyId) {
            jockeyRecords.push({
              ...record,
              position: index + 1,
              timeType,
              raceType
            })
          }
        })
      })
    })

    return jockeyRecords.sort((a, b) => a.position - b.position)
  }

  static getRecordStats(performanceRecords) {
    const stats = {
      monthly: { short: 0, medium: 0, long: 0 },
      allTime: { short: 0, medium: 0, long: 0 },
      totalRecords: 0,
      fastestOverall: null
    }

    if (!performanceRecords) {
      return stats
    }

    let fastestTime = Infinity
    let fastestRecord = null

    // Count records and find fastest overall
    ['monthly', 'allTime'].forEach(timeType => {
      const recordSet = timeType === 'monthly' 
        ? performanceRecords.monthlyRecords 
        : performanceRecords.allTimeRecords

      if (!recordSet) return

      ['short', 'medium', 'long'].forEach(raceType => {
        const count = recordSet[raceType]?.length || 0
        stats[timeType][raceType] = count
        stats.totalRecords += count

        // Check for fastest overall time
        if (recordSet[raceType]?.length > 0 && recordSet[raceType][0].time < fastestTime) {
          fastestTime = recordSet[raceType][0].time
          fastestRecord = {
            ...recordSet[raceType][0],
            raceType,
            timeType
          }
        }
      })
    })

    stats.fastestOverall = fastestRecord
    return stats
  }

  static exportRecords(performanceRecords) {
    // Create exportable format for save games
    if (!performanceRecords) {
      // Creating fresh performance records structure
      return this.initializeRecords()
    }
    
    const result = {
      monthlyRecords: performanceRecords.monthlyRecords ? { ...performanceRecords.monthlyRecords } : { short: [], medium: [], long: [] },
      allTimeRecords: performanceRecords.allTimeRecords ? { ...performanceRecords.allTimeRecords } : { short: [], medium: [], long: [] },
      exportDate: Date.now(),
      version: '1.0'
    }

    // Exporting performance records

    return result
  }

  static importRecords(importData) {
    // Validate and import from save games
    if (!importData) {
      // Initializing fresh records
      return this.initializeRecords()
    }

    // Handle cases where the structure might be incomplete
    const monthlyRecords = importData.monthlyRecords || { short: [], medium: [], long: [] }
    const allTimeRecords = importData.allTimeRecords || { short: [], medium: [], long: [] }

    // Ensure each race type array exists
    const ensureArrays = (records) => ({
      short: Array.isArray(records.short) ? records.short : [],
      medium: Array.isArray(records.medium) ? records.medium : [],
      long: Array.isArray(records.long) ? records.long : []
    })

    const result = {
      monthlyRecords: ensureArrays(monthlyRecords),
      allTimeRecords: ensureArrays(allTimeRecords)
    }

    // Imported performance records

    return result
  }
}